// src/services/liveClasses.js
// Firestore helpers for live class scheduling and management
// Collection: liveClasses
// Schema (post-rebuild — ADR: adr-live-class-rebuild.md):
// {
//   title, description, scheduledAt (ISO), duration (minutes),
//   state ('draft' | 'scheduled' | 'waiting_room_open' | 'live' | 'ending' | 'ended' | 'cancelled'),
//   status (mirror of `state` during migration window — reads prefer `state`, fall back to `status`),
//   muxStreamId, muxStreamKey (admin-only), muxPlaybackId,
//   instructorName, imageEmoji, muscleGroups[], easeIn (bool), strongPlus (bool),
//   endingAt (number | null) — Date.now() when admin clicked End Class; triggers drain window,
//   actualStartTime (number | null) — Date.now() on first transition to `live`,
//   actualEndTime (number | null) — Date.now() on transition to `ended`,
//   cancelledReason ('no_show_timeout' | 'admin_cancelled' | null),
//   cancelledAt (number | null),
//   vodReady (boolean) — set true by mux-webhook when classRecordings doc created,
//   createdAt, updatedAt
// }

import { db, firebaseEnabled } from './firebase';
import { canSeeClass } from './classVisibility';
import {
    collection, doc, getDocs, getDoc, query, orderBy, where, serverTimestamp, increment, onSnapshot, arrayUnion, arrayRemove, limit as fsLimit,
} from 'firebase/firestore';
// Writes wait for the member's Firestore identity (adr-firestore-identity.md a2).
import {
    addDoc, updateDoc, deleteDoc,
} from './firestoreWrite';

// ── Viewer count: increment when a viewer joins ──
export async function joinLiveClass(id) {
    if (!firebaseEnabled || !id) return;
    try {
        await updateDoc(doc(db, 'liveClasses', id), { viewerCount: increment(1) });
    } catch (e) { console.error('Error joining live class:', e); }
}

// ── Viewer count: decrement when a viewer leaves ──
export async function leaveLiveClass(id) {
    if (!firebaseEnabled || !id) return;
    try {
        await updateDoc(doc(db, 'liveClasses', id), { viewerCount: increment(-1) });
    } catch (e) { console.error('Error leaving live class:', e); }
}

// ── Viewer count: subscribe to real-time updates ──
export function subscribeToViewerCount(id, callback) {
    if (!firebaseEnabled || !id) return () => {};
    return onSnapshot(doc(db, 'liveClasses', id), (snap) => {
        if (snap.exists()) {
            callback(snap.data().viewerCount || 0);
        }
    });
}

// ── Fetch all live classes (bounded to past 30 days + all future) ──
// COST OPTIMIZATION (D-068): previously this returned every class ever scheduled,
// which scaled with lifetime class count. Callers (Workouts upcoming grid + admin
// post-end-class refresh) only care about recent + future classes. The 30-day
// backward window is deliberately generous:
//   • Upcoming grid: future classes always included (no backward bound needed).
//   • Post-class engagement: comments stay open indefinitely, so 30d buffers any
//     realistic "I want to comment on a class from last week" flow.
//   • On-Demand/VOD lives in a separate collection (classRecordings) and is
//     unaffected by this cap.
// Classes older than 30 days that are still technically in 'scheduled' state
// would drop out of this query. Mitigation: the no-show auto-cancel cron catches
// stale scheduled classes within 15min of scheduledAt passing, so orphans aged
// >30d are not an ongoing case — any survivors are already stale and won't be
// surfaced in UI regardless.
export async function fetchLiveClasses() {
    if (!firebaseEnabled) return [];
    try {
        const cutoff = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
        const q = query(
            collection(db, 'liveClasses'),
            where('scheduledAt', '>=', cutoff),
            orderBy('scheduledAt', 'asc'),
        );
        const snap = await getDocs(q);
        return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (e) { console.error('Error fetching live classes:', e); return []; }
}

// ── Fetch upcoming classes (scheduled or live) ──
// Uses `status` for the Firestore query (cheapest path — single-field index,
// no composite needed) because dual-write keeps status in sync with state.
// In-memory filter drops any classes whose `state` has advanced to a terminal
// value the query didn't catch (e.g. a doc mid-flight during migration).
export async function fetchUpcomingClasses() {
    if (!firebaseEnabled) return [];
    try {
        const q = query(
            collection(db, 'liveClasses'),
            where('status', 'in', ['scheduled', 'waiting_room_open', 'live', 'ending']),
            orderBy('scheduledAt', 'asc')
        );
        const snap = await getDocs(q);
        return snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((c) => {
                const s = c.state || c.status;
                return s === 'scheduled' || s === 'waiting_room_open' || s === 'live' || s === 'ending';
            });
    } catch (e) { console.error('Error fetching upcoming classes:', e); return []; }
}

// ── Fetch the next upcoming class (cost-bounded single-doc read) ──
// Used by Dashboard's member-only "Your next class" card. Uses limit(1) so
// the query reads at most one Firestore doc regardless of how many future
// classes are scheduled (cost-audit-friendly). The status-IN query matches
// fetchUpcomingClasses semantics; the in-memory state check guards against
// any doc mid-flight during the state/status dual-write migration window.
// `opts.userId` / `opts.isAdmin` drive the visibility allowlist (see
// classVisibility.js). We over-fetch the soonest N candidates instead of a hard
// limit(1) because the very next class may be gated to OTHER users via
// `visibleToUserIds` — in that case the correct answer for this viewer is the
// next class they CAN see, not "no upcoming class." We then return the first
// candidate that both passes `canSeeClass` AND is in a surfaceable state.
// Defaults ({}) reproduce the historical public-only behavior, so the field's
// absence on every pre-feature class is a no-op.
const NEXT_CLASS_CANDIDATE_LIMIT = 10;
export async function fetchNextUpcomingClass(opts = {}) {
    if (!firebaseEnabled) return null;
    const { userId, isAdmin } = opts;
    try {
        const nowIso = new Date().toISOString();
        const q = query(
            collection(db, 'liveClasses'),
            where('status', 'in', ['scheduled', 'waiting_room_open', 'live', 'ending']),
            where('scheduledAt', '>=', nowIso),
            orderBy('scheduledAt', 'asc'),
            fsLimit(NEXT_CLASS_CANDIDATE_LIMIT)
        );
        const snap = await getDocs(q);
        if (snap.empty) return null;
        for (const d of snap.docs) {
            const cls = { id: d.id, ...d.data() };
            const s = cls.state || cls.status;
            // Guards a doc mid-flight during the state/status dual-write migration.
            if (s !== 'scheduled' && s !== 'waiting_room_open' && s !== 'live' && s !== 'ending') continue;
            // Visibility allowlist: skip classes gated to other users.
            if (!canSeeClass(cls, { userId, isAdmin })) continue;
            return cls;
        }
        return null;
    } catch (e) { console.error('Error fetching next upcoming class:', e); return null; }
}

// ── Today's release-day classes (Dashboard "Today's class") ──
// docs/plans/release-day-classes-plan.md. A release-day class drops out of
// fetchNextUpcomingClass at its anchor (noon UTC = ~7 AM Central), which is
// exactly when most members work out — so the Dashboard asks for today's
// releases directly. One equality filter on releaseDate (automatic index); a
// normal day returns 0–2 docs. `todayKey` is the VIEWER's local YYYY-MM-DD.
// Read-only. Returns [] on any failure (the card simply falls back).
export async function fetchReleaseClassesForDay(todayKey, opts = {}) {
    if (!firebaseEnabled || !todayKey) return [];
    const { userId, isAdmin } = opts;
    try {
        const snap = await getDocs(query(collection(db, 'liveClasses'), where('releaseDate', '==', todayKey)));
        return snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((c) => {
                if (c.releaseMode !== 'day') return false;
                const s = c.state || c.status;
                if (s === 'draft' || s === 'cancelled') return false;
                return canSeeClass(c, { userId, isAdmin });
            })
            .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    } catch (e) { console.error('Error fetching release classes:', e); return []; }
}

// ── Fetch a single class ──
export async function fetchLiveClass(id) {
    if (!firebaseEnabled || !id) return null;
    try {
        const snap = await getDoc(doc(db, 'liveClasses', id));
        if (!snap.exists()) return null;
        return { id: snap.id, ...snap.data() };
    } catch (e) { console.error('Error fetching live class:', e); return null; }
}

// ── Dual-write helper: every state change writes BOTH `state` and `status` ──
// During the migration window (2 releases) we dual-write so cached clients
// reading `status` still see correct values. Reads prefer `state` via the
// classState.js helpers. Drop the `status` dual-write after 2 release cycles.
function withDualState(data) {
    const out = { ...data };
    if ('state' in data && !('status' in data)) {
        out.status = data.state;
    } else if ('status' in data && !('state' in data)) {
        out.state = data.status;
    }
    return out;
}

// ── Create a new class ──
// classType: 'live' (default) or 'prerecorded'
// For prerecorded classes, uploadStatus tracks the video upload lifecycle
//
// Honors `data.state === 'draft'` to create a class in the draft state. Drafts
// are filtered out of every user-facing view (upcoming, past, isAiredThisWeek) and
// every cron query (state === 'scheduled' / 'live' / 'ending') — so they're
// invisible until explicitly published via publishDraftClass(). Default
// behavior (no `state` passed) is unchanged: `state: 'scheduled'`.
export async function createLiveClass(data) {
    if (!firebaseEnabled) return null;
    try {
        const initialState = data.state === 'draft' ? 'draft' : 'scheduled';
        const classData = {
            ...data,
            state: initialState,
            status: initialState, // dual-write for migration window
            classType: data.classType || 'live',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        };
        // Pre-recorded classes start with no upload
        if (classData.classType === 'prerecorded') {
            classData.uploadStatus = data.uploadStatus || 'pending';
            classData.muxAssetId = data.muxAssetId || null;
        }
        const ref = await addDoc(collection(db, 'liveClasses'), classData);
        return ref.id;
    } catch (e) { console.error('Error creating live class:', e); return null; }
}

// ── Re-air a past class ──
// Schedules an ALREADY-RECORDED class to air again on the Schedule.
//
// This is deliberately NOT a new kind of class. It creates a bog-standard
// `classType: 'prerecorded'` doc that points at the EXISTING Mux asset, so the
// entire proven pre-recorded pipeline runs untouched: the cron flips
// scheduled→live at scheduledAt, LivePlayer seeks late joiners to the cohort
// position, reactions/presence/comments work, and the cron flips live→ended at
// scheduledAt + videoDurationSeconds. Nothing about the airing path is special-cased.
//
// It can never create a second On Demand entry: `classRecordings` docs are keyed
// BY muxAssetId, and the cron's recording-creation block is guarded by
// `existingRec.empty` — a re-air finds the existing recording and skips creation.
// The recording keeps its original aired date and its place in On Demand.
//
// `recording` is a classRecordings doc (or the Workouts `classVideos` shape, which
// spreads it). Returns the new class id, or null if the recording can't be aired.
export async function createReAirClass(recording, overrides = {}) {
    if (!firebaseEnabled || !recording) return null;
    // No Mux asset ⇒ nothing to air. Guards legacy/orphaned recordings and any
    // record whose asset was deleted from Mux.
    if (!recording.muxAssetId || !recording.muxPlaybackId) return null;

    // Prefer the authoritative recorded length — the cron uses videoDurationSeconds
    // (when > 30s) to decide the live→ended transition, so this is what actually
    // determines when the re-air finishes.
    const durationSeconds = recording.durationSeconds
        || (recording.duration ? recording.duration * 60 : 0);

    return createLiveClass({
        title: recording.title || '',
        description: recording.description || '',
        duration: recording.duration || Math.max(1, Math.round(durationSeconds / 60)) || 45,
        classType: 'prerecorded',
        // Point at the existing asset. uploadStatus 'ready' + muxAssetId is exactly
        // what the cron's scheduled→live gate looks for, and it's also what makes the
        // admin tile render as video-ready instead of offering "Record Class"
        // (that button is gated on !cls.muxAssetId).
        muxAssetId: recording.muxAssetId,
        muxPlaybackId: recording.muxPlaybackId,
        uploadStatus: 'ready',
        videoDurationSeconds: durationSeconds,
        thumbnailUrl: recording.thumbnailUrl || '',
        imageUrl: '',
        muscleGroups: recording.groups || [],
        easeIn: recording.easeIn || false,
        strongPlus: recording.strongPlus || false,
        price: recording.isFree ? 0 : 1,
        // Carry the visibility allowlist so a gated recording's re-air stays gated.
        visibleToUserIds: recording.visibleToUserIds || [],
        // Forensic breadcrumb — which recording this airing was built from. Not read
        // by any runtime path; the aired-tile lookup uses vodAssetId (stamped by the
        // cron) so this stays purely informational.
        sourceRecordingId: recording.id || recording.muxAssetId,
        ...overrides,
    });
}

// ── Publish a draft class (state: 'draft' → 'scheduled') ──
// Single Firestore write that flips state + status (dual-write maintained) and
// stamps publishedAt. From this point forward every existing flow takes over:
// reminders cron picks it up, Schedule tab renders it, pre-recorded auto-
// transitions to live at scheduledAt, classRecordings doc is created on end.
export async function publishDraftClass(id) {
    return updateLiveClass(id, {
        state: 'scheduled',
        publishedAt: Date.now(),
    });
}

// ── Update a class ──
export async function updateLiveClass(id, data) {
    if (!firebaseEnabled || !id) return false;
    try {
        await updateDoc(doc(db, 'liveClasses', id), {
            ...withDualState(data),
            updatedAt: serverTimestamp(),
        });
        return true;
    } catch (e) { console.error('Error updating live class:', e); return false; }
}

// NOTE (2026-10-06): there is deliberately NO client function that moves a
// pre-recorded class between scheduled / live / ended. Members' devices only
// DISPLAY airtime (Workouts.js withAirtime); the server cron is the sole writer.
// Do not add one back — see the 2026-10-05 incident in CLAUDE.md.

// ── Delete a class ──
export async function deleteLiveClass(id) {
    if (!firebaseEnabled || !id) return false;
    try {
        await deleteDoc(doc(db, 'liveClasses', id));
        return true;
    } catch (e) { console.error('Error deleting live class:', e); return false; }
}

// ── Mark class as live (+ stamp actualStartTime) ──
export async function markClassLive(id) {
    return updateLiveClass(id, { state: 'live', actualStartTime: Date.now() });
}

// ── Mark class as in the drain window (End Class Phase 1) ──
// Sets `state: 'ending'` and `endingAt`. Viewer-side listeners observe this
// and transition into graceful-ending playback. `endingAt` is written here
// (if not already in data) so the caller can stamp it consistently.
export async function markClassEnding(id, endingAt = Date.now()) {
    return updateLiveClass(id, { state: 'ending', endingAt });
}

// ── Mark class as ended (+ stamp actualEndTime) ──
export async function markClassEnded(id) {
    return updateLiveClass(id, { state: 'ended', actualEndTime: Date.now() });
}

// ── Mark class as cancelled (admin delete OR cron no-show) ──
// reason: 'admin_cancelled' | 'no_show_timeout'
export async function markClassCancelled(id, reason = 'admin_cancelled') {
    return updateLiveClass(id, {
        state: 'cancelled',
        cancelledAt: Date.now(),
        cancelledReason: reason,
    });
}

// ── Health log event writer (D-057) ──
// Writes a small doc to liveClasses/{classId}/healthLog for post-incident
// forensics. Non-blocking — errors are swallowed so a failed log write never
// affects class lifecycle.
export async function logHealthEvent(classId, event, metadata = {}, severity = 'info') {
    if (!firebaseEnabled || !classId || !event) return;
    try {
        const sub = collection(db, 'liveClasses', classId, 'healthLog');
        await addDoc(sub, {
            event,
            severity,
            timestamp: new Date().toISOString(),
            metadata,
        });
    } catch (e) { /* non-critical — healthLog is forensic only */ }
}

// ── Helper: format class time for display ──
export function formatClassTime(isoString) {
    if (!isoString) return '';
    const d = new Date(isoString);
    return d.toLocaleDateString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric',
    }) + ' at ' + d.toLocaleTimeString('en-US', {
        hour: 'numeric', minute: '2-digit', hour12: true,
    });
}

// ── Presence: add viewer identity to class doc ──
export async function joinPresence(id, userId, firstName, lastInitial) {
    if (!firebaseEnabled || !id || !userId) return;
    try {
        const viewer = { userId, firstName: firstName || '', lastInitial: lastInitial || '' };
        await updateDoc(doc(db, 'liveClasses', id), { viewers: arrayUnion(viewer) });
    } catch (e) { /* non-critical — viewer count still works */ }
}

// ── Presence: remove viewer identity from class doc ──
export async function leavePresence(id, userId, firstName, lastInitial) {
    if (!firebaseEnabled || !id || !userId) return;
    try {
        const viewer = { userId, firstName: firstName || '', lastInitial: lastInitial || '' };
        await updateDoc(doc(db, 'liveClasses', id), { viewers: arrayRemove(viewer) });
    } catch (e) { /* non-critical */ }
}

// ── Subscribe to viewer list (piggybacks on existing class doc listener) ──
export function subscribeToViewers(id, callback) {
    if (!firebaseEnabled || !id) return () => {};
    return onSnapshot(doc(db, 'liveClasses', id), (snap) => {
        if (snap.exists()) {
            callback(snap.data().viewers || []);
        }
    });
}

// ── Helper: is a class happening now or within N minutes ──
export function isClassSoon(scheduledAt, withinMinutes = 15) {
    if (!scheduledAt) return false;
    const now = new Date();
    const start = new Date(scheduledAt);
    const diff = (start - now) / 1000 / 60; // minutes until class
    return diff <= withinMinutes && diff >= -120; // within window, not more than 2h past
}
